import styled from 'styled-components'
import { formataCPF } from '../../utils/cpfMask'
import { colors } from '../../theme'

/**
 * Lista de CPFs de vendedores acompanhados por um usuário (PD-0004).
 *
 * Usada pelos papéis COORDENADOR e ASSISTENTE, que enxergam os clientes de vários vendedores.
 * VENDEDOR continua com um único CPF (campo simples) e ADMINISTRADOR não usa CPF.
 *
 * O componente é controlado: guarda os CPFs já formatados e devolve a lista inteira a cada
 * alteração. A validação de "11 dígitos" fica com quem usa (o backend também valida).
 */
const Bloco = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Linha = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  input {
    flex: 1;
    height: 42px;
    border-radius: 12px;
    border: 1px solid ${colors.grey300};
    padding: 0 12px;
    font-size: 14px;
    box-sizing: border-box;
  }
`

const Rotulo = styled.span`
  color: ${colors.grey900};
  font-size: 14px;
`

const Ajuda = styled.span`
  color: ${colors.grey600};
  font-size: 12px;
`

const BtnRemover = styled.button`
  background: none;
  border: 1px solid ${colors.grey300};
  border-radius: 10px;
  color: ${colors.errorText};
  cursor: pointer;
  font-size: 18px;
  height: 42px;
  line-height: 1;
  width: 42px;
`

const BtnAdicionar = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: ${colors.primaryText};
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  text-decoration: underline;
`

const Erro = styled.span`
  color: ${colors.errorText};
  font-size: 13px;
`

type CpfsVendedoresProps = {
  cpfs: string[]
  onChange: (cpfs: string[]) => void
  idPrefix: string
  erro?: string
}

export function CpfsVendedores({
  cpfs,
  onChange,
  idPrefix,
  erro,
}: CpfsVendedoresProps) {
  const lista = cpfs.length > 0 ? cpfs : ['']

  const alteraCpf = (index: number, valor: string) =>
    onChange(lista.map((cpf, i) => (i === index ? formataCPF(valor) : cpf)))

  const removeCpf = (index: number) =>
    onChange(lista.filter((_, i) => i !== index))

  return (
    <Bloco>
      <Rotulo>CPFs dos vendedores acompanhados</Rotulo>
      <Ajuda>
        O usuário verá os clientes de todos os vendedores listados aqui.
      </Ajuda>
      {lista.map((cpf, index) => (
        <Linha key={`${idPrefix}-cpf-${index + 1}`}>
          <input
            id={`${idPrefix}-cpf-${index}`}
            type="text"
            value={cpf}
            placeholder="000.000.000-00"
            onChange={event => alteraCpf(index, event.target.value)}
          />
          {lista.length > 1 && (
            <BtnRemover
              type="button"
              title="Remover CPF"
              onClick={() => removeCpf(index)}
            >
              ×
            </BtnRemover>
          )}
        </Linha>
      ))}
      <BtnAdicionar type="button" onClick={() => onChange([...lista, ''])}>
        + Adicionar CPF
      </BtnAdicionar>
      {erro ? <Erro>{erro}</Erro> : ''}
    </Bloco>
  )
}
